import React, { useState } from 'react';
import { animated } from '@react-spring/web';
import { Spacing16, Spacing24, Spacing40, Spacing8 } from 'autogen/design-tokens/tokens';
import type { ProductListItemModel } from 'autogen/swagger/all';
import styled, { css } from 'styled-components';
import ProductItem from 'components/shared/productItem';
import { breakpoint, sizes } from 'variables';
import useContainerQuery, { type BreakpointObject } from 'hooks/responsive/useContainerQuery';
import { ShowMore } from 'autogen/translation-keys/trans-website-recommendation';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/Button';
import UAEventTracking, { type TrackingCategory } from 'modules/tracking/UAEventTracking';
import HorizontalProductItem from 'components/shared/productItem/components/HorizontalProductItem/HorizontalProductItem';
import { firstCharacterToLower } from 'modules/helpers/strings';
import { GA4Tracking } from 'modules/tracking/GA4';
import { getPrettifiedGrid } from '../../../productRecommendation/shared/helpers';

type ItemConfigTypes = 'horizontalProductItem' | 'verticalProductItem';
type ItemConfig<T> = {
    component: any; // change to AnimatedComponent<React.ForwardRefExoticComponent<T>>>
    breakpoints: BreakpointObject<T>;
    defaultBreakpointValue: T;
    rowsPerLevel: number;
    minWidth: string;
    maxWidth: string;
};
type ItemConfigs<T> = {
    [key in ItemConfigTypes]: ItemConfig<T>;
};

const AnimatedVerticalItem = animated<any>(ProductItem);
const AnimatedHorizontalProductItem = animated<any>(HorizontalProductItem);

const itemConfigs: ItemConfigs<number> = {
    horizontalProductItem: {
        component: AnimatedHorizontalProductItem,
        breakpoints: { [sizes.xs]: 1, [sizes.sm]: 2, [sizes.md]: 2, [sizes.lg]: 3, [sizes.xl]: 3, [sizes.xxl]: 4 },
        defaultBreakpointValue: 2,
        rowsPerLevel: 3,
        minWidth: '280px',
        maxWidth: '1fr'
    },
    verticalProductItem: {
        component: AnimatedVerticalItem,
        breakpoints: { [sizes.xs]: 2, [sizes.sm]: 2, [sizes.md]: 3, [sizes.lg]: 4, [sizes.xl]: 5, [sizes.xxl]: 6 },
        defaultBreakpointValue: 2,
        rowsPerLevel: 2,
        minWidth: '132px',
        maxWidth: '1fr'
    }
};

/* #region  styles */
const Grid = styled.div<{
    $columns: number;
    $itemMaxWidth: ItemConfig<number>['maxWidth'];
    $itemMinWidth: ItemConfig<number>['minWidth'];
    $itemConfigType?: ItemConfigTypes;
}>`
    display: grid;
    column-gap: ${({ $itemConfigType }) => ($itemConfigType === 'horizontalProductItem' ? Spacing8 : Spacing16)};
    row-gap: ${({ $itemConfigType }) => ($itemConfigType === 'horizontalProductItem' ? Spacing8 : Spacing24)};
    grid-auto-flow: dense;
    width: 100%;
    justify-content: center;
    ${({ $columns, $itemMaxWidth, $itemMinWidth }) =>
        css`
            grid-template-columns: repeat(${$columns}, minmax(${$itemMinWidth}, ${$itemMaxWidth}));
        `}

    ${breakpoint.up(sizes.lg)} {
        gap: ${({ $itemConfigType }) => ($itemConfigType === 'horizontalProductItem' ? Spacing16 : Spacing24)};
    }
`;

const Container = styled.div`
    display: grid;
    gap: ${Spacing24};
    align-items: center;
    justify-items: center;

    ${breakpoint.up(sizes.lg)} {
        gap: ${Spacing40};
    }
`;

/* #endregion */

type PossibleTrackingCategories = Extract<
    TrackingCategory,
    | 'basketRecomendation'
    | 'cmsPageRecomendation'
    | 'homePageRecomendation'
    | 'powerstep'
    | 'productGroupRecomendation'
    | 'productRecomendation'
    | 'searchResultPageRecomendation'
    | 'zeroResultPageRecomendation'
>;

export default function ProductGridWithLevels({
    itemConfigType = 'verticalProductItem',
    items,
    ...restProps
}: {
    itemConfigType?: ItemConfigTypes;
    items: ProductListItemModel[];
}) {
    const { t } = useTranslation();
    const itemConfig = itemConfigs[itemConfigType];
    const ItemComponent = itemConfig.component;

    const [ref, columns] = useContainerQuery<number>(itemConfig.breakpoints, itemConfig.defaultBreakpointValue);
    const [level, setLevel] = useState(1);

    const visibleItems = getPrettifiedGrid(items, columns, itemConfig.rowsPerLevel, level);

    /*
        We need to add classNames and overwrite styling because we don't have a way to differentiate the recommendation on different pages, they are all loaded in using the same generic display template "ProductRecommendationsModel".
        In the future we want to add the Recommendation react component directly to the related razor view e.g powerstep recommendation might use a version called PowerstepProductRecommendationGrid if it's styling
        is a lot different than the other kind of productRecommendationGrid components
    */

    if (!items.length) return null;

    return (
        <Container ref={ref} className="recommendation-grid" {...restProps}>
            <Grid
                $columns={columns}
                $itemMaxWidth={itemConfig.maxWidth}
                $itemMinWidth={itemConfig.minWidth}
                $itemConfigType={itemConfigType}
            >
                {visibleItems.map((item) => (
                    <ItemComponent model={item} key={item.id} />
                ))}
            </Grid>

            {visibleItems.length !== items.length ? (
                <Button
                    onClick={() => {
                        setLevel((prevState) => prevState + 1);
                        UAEventTracking({
                            eventCategory: firstCharacterToLower(
                                items[0].uaTrackingCategory
                            ) as PossibleTrackingCategories,
                            eventAction: 'clickedExpandRecommendedItems',
                            eventLabel: `Expand_lvl_${level}`
                        });

                        GA4Tracking({
                            eventName: 'expand_recommended_items',
                            context: 'recommendation',
                            category: 'navigation',
                            eventParams: [
                                {
                                    param_name: 'recommendation_type',
                                    param_value: firstCharacterToLower(items[0].uaTrackingCategory) // This value may not match data in spreadsheet
                                }
                            ]
                        });
                    }}
                    variant="primaryText"
                >
                    {t(ShowMore)}
                </Button>
            ) : null}
        </Container>
    );
}
